/**
 * Firebase Client SDK initialization
 *
 * This module initializes the Firebase SDK for use in browser environments
 * (web and CMS frontend). It should NOT be used in Node.js environments
 * (Cloud Functions or backend APIs).
 *
 * @example
 * import { initializeClientSDK, getClientAuth, getClientDB } from '@canvas/firebase-config/client';
 *
 * // Initialize once in your app entry point
 * const { auth, db, storage } = initializeClientSDK();
 *
 * // Then use in components
 * const user = await getClientAuth().currentUser;
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth, connectAuthEmulator } from 'firebase/auth';
import {
  getFirestore,
  type Firestore,
  connectFirestoreEmulator,
  disableNetwork,
  enableNetwork,
} from 'firebase/firestore';
import { getStorage, type FirebaseStorage, connectStorageEmulator } from 'firebase/storage';
import { getAnalytics, type Analytics } from 'firebase/analytics';
import {
  getClientConfig,
  shouldUseEmulators,
  getFirestoreHost,
  getAuthEmulatorUrl,
  getStorageBucketUrl,
  AUTH_CONFIG,
  isDevelopment,
} from './config';

/**
 * Client SDK instances
 * Private variables to maintain singleton pattern
 */
let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;
let analytics: Analytics | null = null;

let isInitialized = false;
let initializationError: Error | null = null;

/**
 * Initialize Firebase client SDK
 *
 * This function should be called once when your app starts,
 * typically in your main.tsx or app.tsx file.
 *
 * @example
 * // In apps/web/src/main.tsx
 * import { initializeClientSDK } from '@canvas/firebase-config/client';
 *
 * const { auth, db, storage } = initializeClientSDK();
 * ReactDOM.createRoot(document.getElementById('root')!).render(<App />);
 *
 * @returns Object containing initialized Firebase services
 * @throws Error if Firebase config is invalid
 */
export function initializeClientSDK() {
  // Return cached instance if already initialized
  if (isInitialized && app) {
    return { auth: auth!, db: db!, storage: storage!, analytics: analytics };
  }

  try {
    // Get Firebase config from environment variables
    const config = getClientConfig();

    // Initialize Firebase app
    app = initializeApp(config);

    // Initialize Auth
    auth = getAuth(app);

    // Set persistence strategy (how long to keep user logged in)
    if (typeof window !== 'undefined') {
      // Only in browser environment
      if (AUTH_CONFIG.PERSISTENCE_TYPE === 'LOCAL') {
        // Keep user logged in indefinitely
      } else if (AUTH_CONFIG.PERSISTENCE_TYPE === 'SESSION') {
        // Log out when browser tab closes
      }
      // 'NONE' logs out on page refresh
    }

    // Initialize Firestore
    db = getFirestore(app);

    // Initialize Storage
    storage = getStorage(app);

    // Initialize Analytics (if measurement ID provided)
    if (config.measurementId) {
      try {
        analytics = getAnalytics(app);
      } catch (error) {
        console.warn('Analytics initialization failed (non-critical):', error);
      }
    }

    // Connect to emulators only when explicitly enabled
    if (shouldUseEmulators()) {
      connectToEmulators();
    } else {
      console.log(`✓ Firebase connected to production project: ${config.projectId}`);
    }

    isInitialized = true;
    console.log('✓ Firebase client SDK initialized successfully');

    return {
      app,
      auth,
      db,
      storage,
      analytics: analytics || null,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    initializationError = error instanceof Error ? error : new Error(message);
    console.error('✗ Firebase client initialization failed:', message);

    // In development, warn but don't crash
    if (isDevelopment()) {
      console.warn(
        'Firebase is not properly configured. Please check your .env.local file and ensure all VITE_FIREBASE_* variables are set.'
      );
    }

    throw initializationError;
  }
}

/**
 * Connect to Firebase emulators (for local development)
 *
 * @private
 */
function connectToEmulators() {
  if (!auth || !db || !storage) {
    throw new Error('Firebase services not initialized');
  }

  try {
    const authEmulatorUrl = getAuthEmulatorUrl();
    const firestoreHost = getFirestoreHost();
    const storageHost = getStorageBucketUrl();

    // Connect Auth emulator
    if (authEmulatorUrl && !auth.emulatorConfig) {
      console.log(`🔧 Connecting to Auth emulator at ${authEmulatorUrl}`);
      connectAuthEmulator(auth, authEmulatorUrl, {
        disableWarnings: true,
      });
    }

    // Connect Firestore emulator. There's no public API to check whether
    // this has already run (the SDK just throws if the Firestore instance
    // is already in use), so guard with try/catch instead of reaching into
    // private internals — that also keeps a double-connect here from
    // skipping the Storage emulator connection below.
    if (firestoreHost) {
      try {
        const [host, port] = firestoreHost.split(':');
        console.log(`🔧 Connecting to Firestore emulator at ${firestoreHost}`);
        connectFirestoreEmulator(db, host, parseInt(port, 10));
      } catch (error) {
        console.warn('⚠ Firestore emulator connection skipped (already connected?):', error);
      }
    }

    // Connect Storage emulator
    if (storageHost && !storage.app.options.storageBucket?.includes('emulator')) {
      const [host, port] = storageHost.split(':');
      console.log(`🔧 Connecting to Storage emulator at ${storageHost}`);
      connectStorageEmulator(storage, host, parseInt(port, 10));
    }

    console.log('✓ Firebase emulators connected');
  } catch (error) {
    // Emulator connection is optional - app should work without it
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.warn('⚠ Failed to connect to Firebase emulators (continuing without them):', message);
  }
}

/**
 * Get the Auth service
 *
 * @example
 * const auth = getClientAuth();
 * const user = auth.currentUser;
 *
 * @throws Error if SDK not initialized
 */
export function getClientAuth(): Auth {
  if (!auth) {
    throw new Error(
      'Firebase Auth not initialized. Call initializeClientSDK() first.'
    );
  }
  return auth;
}

/**
 * Get the Firestore database service
 *
 * @example
 * const db = getClientDB();
 * const snapshot = await getDocs(collection(db, 'projects'));
 *
 * @throws Error if SDK not initialized
 */
export function getClientDB(): Firestore {
  if (!db) {
    throw new Error(
      'Firestore not initialized. Call initializeClientSDK() first.'
    );
  }
  return db;
}

/**
 * Get the Storage service
 *
 * @example
 * const storage = getClientStorage();
 * const fileRef = ref(storage, 'path/to/file');
 *
 * @throws Error if SDK not initialized
 */
export function getClientStorage(): FirebaseStorage {
  if (!storage) {
    throw new Error(
      'Storage not initialized. Call initializeClientSDK() first.'
    );
  }
  return storage;
}

/**
 * Get the Firebase app instance
 *
 * @throws Error if SDK not initialized
 */
export function getClientApp(): FirebaseApp {
  if (!app) {
    throw new Error(
      'Firebase app not initialized. Call initializeClientSDK() first.'
    );
  }
  return app;
}

/**
 * Get Analytics service (optional)
 *
 * @returns Analytics service or null if not available
 */
export function getClientAnalytics(): Analytics | null {
  return analytics || null;
}

/**
 * Check if SDK is initialized
 */
export function isClientSDKInitialized(): boolean {
  return isInitialized && app !== null;
}

/**
 * Get initialization error (if any)
 */
export function getInitializationError(): Error | null {
  return initializationError;
}

/**
 * Reset initialization (useful for testing)
 *
 * @internal
 */
export function resetClientSDK() {
  app = null;
  auth = null;
  db = null;
  storage = null;
  analytics = null;
  isInitialized = false;
  initializationError = null;
}

/**
 * Enable/disable network connectivity
 * Useful for offline-first applications
 *
 * @example
 * // Disable network to work offline
 * await setOfflineMode(true);
 * // ... make queries, they'll use local cache
 *
 * // Re-enable network
 * await setOfflineMode(false);
 */
export async function setOfflineMode(offline: boolean) {
  const database = getClientDB();
  if (offline) {
    await disableNetwork(database);
  } else {
    await enableNetwork(database);
  }
}

/** Alias for initializeClientSDK */
export const initFirebase = initializeClientSDK;
